import type {
  ClientCancellationLineDto,
  ClientCancellationLineQueueItemDto,
  ClientCancellationLineReason,
  ClientCancellationLineStatus,
  ClientCancellationLineStatusHistoryEntryDto,
  ClientCancellationSuspensionPolicyDto,
} from '../../types/clientCancellationLines';
import { normalizarCancelamento1056Desfecho } from '../posVenda/orientarCancelamento1056Desfecho';
import { CLIENT_CANCELLATION_SUSPENSION_BLOCK_MESSAGE } from './clientCancellationLinesLabels';

function asReason(value: unknown): ClientCancellationLineReason {
  if (value === 'Zerada' || value === 'SemUso0a4Meses' || value === 'Outro') return value;
  return 'Outro';
}

function asStatus(value: unknown): ClientCancellationLineStatus {
  const allowed: ClientCancellationLineStatus[] = [
    'Agendado',
    'ClienteAvisado',
    'CancelamentoConfirmado',
    'NaoCancelou',
    'Encerrado',
  ];
  if (typeof value === 'string' && (allowed as string[]).includes(value)) {
    return value as ClientCancellationLineStatus;
  }
  return 'Agendado';
}

function normalizarHistorico(raw: unknown): ClientCancellationLineStatusHistoryEntryDto[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const e = entry as Record<string, unknown>;
    return {
      status: asStatus(e.status),
      notes: (e.notes as string | null | undefined) ?? null,
      changedAtUtc: String(e.changedAtUtc ?? e.changedAt ?? ''),
      changedByUserName: (e.changedByUserName as string | null | undefined) ?? null,
    };
  });
}

export function normalizarClientCancellationLine(raw: unknown): ClientCancellationLineDto {
  const r = raw as Record<string, unknown>;
  return {
    id: String(r.id ?? ''),
    contractId: String(r.contractId ?? ''),
    contractLineId: String(r.contractLineId ?? ''),
    ddd: String(r.ddd ?? ''),
    numeroLinha: String(r.numeroLinha ?? r.numero ?? ''),
    reason: asReason(r.reason),
    plannedDate: String(r.plannedDate ?? r.dataPlanejada ?? '').slice(0, 10),
    notes: (r.notes as string | null | undefined) ?? null,
    status: asStatus(r.status),
    statusHistory: normalizarHistorico(r.statusHistory ?? r.historicoStatus),
    createdAtUtc: String(r.createdAtUtc ?? r.createdAt ?? ''),
    createdByUserName: (r.createdByUserName as string | null | undefined) ?? null,
    protocolId:
      (r.protocolId as string | null | undefined) ??
      (r.protocoloId as string | null | undefined) ??
      null,
    protocolNumber:
      (r.protocolNumber as string | null | undefined) ??
      (r.protocoloNumero as string | null | undefined) ??
      null,
    desfecho: normalizarCancelamento1056Desfecho(r.desfecho),
  };
}

export function normalizarClientCancellationQueueItem(raw: unknown): ClientCancellationLineQueueItemDto {
  const base = normalizarClientCancellationLine(raw);
  const r = raw as Record<string, unknown>;
  return {
    ...base,
    contractNumber: (r.contractNumber as string | null | undefined) ?? null,
    clientId: String(r.clientId ?? ''),
    clientRazaoSocial: String(r.clientRazaoSocial ?? r.clientName ?? ''),
    clientCnpj: (r.clientCnpj as string | null | undefined) ?? null,
  };
}

export function normalizarClientCancellationLines(raw: unknown): ClientCancellationLineDto[] {
  if (Array.isArray(raw)) return raw.map(normalizarClientCancellationLine);
  const obj = raw as { items?: unknown[]; data?: unknown[] };
  const list = obj.items ?? obj.data ?? [];
  return Array.isArray(list) ? list.map(normalizarClientCancellationLine) : [];
}

export function normalizarClientCancellationQueue(raw: unknown): ClientCancellationLineQueueItemDto[] {
  if (Array.isArray(raw)) return raw.map(normalizarClientCancellationQueueItem);
  const obj = raw as { items?: unknown[] };
  const list = obj.items ?? [];
  return Array.isArray(list) ? list.map(normalizarClientCancellationQueueItem) : [];
}

export function normalizarSuspensionBlockStatus(raw: unknown): ClientCancellationSuspensionPolicyDto {
  const r = raw as Record<string, unknown>;
  const legacyBlock =
    r.bloqueioSuspensaoAtivo === true ||
    r.suspensionBlockActive === true ||
    r.blocked === true ||
    r.isActive === true;
  const blockNewSuspensions =
    r.blockNewSuspensions === true ||
    legacyBlock ||
    r.blockNewSuspensions === 'true';
  const cutoff = String(
    r.suspensionCutoffDate ?? r.cutoffDate ?? r.dataCorte ?? '2026-11-03'
  ).slice(0, 10);
  const description =
    (r.policyReferenceDateDescription as string | null | undefined) ??
    (r.policyDescription as string | null | undefined) ??
    null;
  const message =
    (typeof r.message === 'string' && r.message.trim()) ||
    CLIENT_CANCELLATION_SUSPENSION_BLOCK_MESSAGE;
  return {
    blockNewSuspensions,
    suspensionCutoffDate: cutoff,
    policyReferenceDateDescription: description,
    message,
  };
}
